export interface Volume {
  icon: string
  number: string
  title: string
  subtitle: string
  problem: string[]
  chain?: string
  afterChain?: string[]
  bulletsIntro?: string
  bullets: string[]
  afterBullets?: string
  result: string
  popular?: boolean
}

export const volumes: Volume[] = [
  {
    icon: '📘',
    number: 'Volume 1',
    title: 'Judo Foundations & Athlete Development',
    subtitle: 'Build the technical and coaching foundation behind better Judo',
    problem: [
      'Before combinations, tactics or advanced Randori can work, athletes need posture, movement, Ukemi, balance, connection and strong technical fundamentals.',
      'This volume gives you a structured way to build those foundations progressively instead of improvising every early-stage session.',
    ],
    bullets: [
      'Posture, stance and movement fundamentals',
      'Ukemi development',
      'Partner connection and balance',
      'Introductory grip fundamentals',
      'Foundational Kuzushi',
      'Core movement habits for developing judoka',
      'Early technical progressions',
      'Beginner-to-developing athlete structure',
      'Coaching observations and correction priorities',
    ],
    result:
      'Your judoka build stronger foundations and you teach them through a repeatable structure instead of guessing what should come first.',
  },
  {
    icon: '🥋',
    number: 'Volume 2',
    title: 'Throwing Mechanics, Kuzushi & Technical Progressions',
    subtitle: 'A complete visual coaching system for teaching how throws really work',
    problem: [
      'A throw is not just the final moment of projection.',
      'This volume helps you teach the full technical chain:',
    ],
    chain: 'Grip → Movement → Kuzushi → Tsukuri → Kake → Control',
    afterChain: [
      'Instead of telling athletes to “throw harder,” you can break the movement down visually and coach the exact part that needs improvement.',
    ],
    bullets: [
      'Happo-no-kuzushi and balance-breaking concepts',
      'Weight transfer and timing',
      'Movement into throwing positions',
      'Ashi-waza development',
      'De-ashi-barai',
      'Ko-uchi-gari',
      'O-uchi-gari',
      'Osoto-gari',
      'O-goshi',
      'Uki-goshi',
      'Harai-goshi',
      'Ippon-seoi-nage',
      'Tai-otoshi',
      'Uchi-mata',
      'Step-by-step visual throw breakdowns',
      'Correct-vs-incorrect comparisons',
      'Technical progressions and error correction',
    ],
    result:
      'You stop teaching throws as isolated actions and start teaching the mechanics, timing and positioning that make them functional.',
  },
  {
    icon: '🔄',
    number: 'Volume 3',
    title: 'Throwing Systems, Combinations & Reactions',
    subtitle: 'Teach athletes to connect attacks instead of memorizing isolated techniques',
    problem: ['A real combination is not just Technique A followed by Technique B.', 'It is:'],
    chain: 'Attack → Reaction → Read → Continue or Reset',
    afterChain: [
      'This volume gives you a complete visual system for teaching how attacks connect, how reactions create openings and how athletes make better decisions.',
    ],
    bullets: [
      'Reaction reading',
      'Weight-shift recognition',
      'Heavy-foot / light-foot concepts',
      'Forward ↔ backward attack systems',
      'Inside ↔ outside attack systems',
      'Ko-uchi → Seoi systems',
      'Osoto → O-uchi systems',
      'O-uchi → Uchi-mata systems',
      'Ashi-waza chains',
      'Directional changes',
      'Rhythm changes',
      'Combination drills',
      'Decision-making drills',
      'Situational applications',
      'Reset logic and constrained Randori',
    ],
    result:
      'Your athletes begin understanding why the next attack becomes available instead of simply memorizing combinations.',
  },
  {
    icon: '🔵',
    number: 'Volume 4',
    title: 'Newaza, Transitions & Groundwork Systems',
    subtitle: 'Turn groundwork into a structured part of your coaching system',
    problem: [
      'Newaza often becomes random rolling unless the coach has a clear framework.',
      'This volume helps you teach groundwork through positions, transitions, control and practical progression.',
    ],
    bullets: [
      'Fundamental groundwork positions',
      'Control and pinning systems',
      'Escapes and defensive structure',
      'Transition pathways',
      'Standing-to-ground connections',
      'Newaza progressions',
      'Situational groundwork',
      'Common errors and practical corrections',
      'Coaching cues for positional development',
    ],
    result:
      'Groundwork becomes a teachable progression instead of an unstructured part of training.',
  },
  {
    icon: '🟣',
    number: 'Volume 5',
    title: 'Gripping, Tactics, Counters & Randori Development',
    subtitle: 'Build smarter Judo — not random Randori',
    problem: [
      'Randori should not be chaos.',
      'It should be a learning environment where athletes improve timing, decisions, tactical awareness and technical application.',
      'This volume helps you structure that process progressively.',
    ],
    bullets: [
      'Grip connection and Kumikata fundamentals',
      'Movement from the grip',
      'Attack angles',
      'Tactical positioning',
      'Reaction reading',
      'Attack / counter relationships',
      'Situational Randori',
      'Constrained Randori',
      'Decision-making systems',
      'Reset logic',
      'Coach observation and correction systems',
    ],
    result: 'Randori becomes a meaningful development tool instead of just another hard round.',
  },
  {
    icon: '📚',
    number: 'Volume 6',
    title: '150 Ready-to-Coach Judo Sessions',
    subtitle: 'Stop building every session from scratch',
    problem: [
      'This is the practical heart of the entire 6-in-1 system.',
      'You receive 150 complete ready-to-coach Judo sessions, organized across the major areas of coaching so you can open the system, choose the objective and begin immediately.',
    ],
    bulletsIntro: 'Inside the session library, you’ll find sessions built around areas such as:',
    bullets: [
      'Judo foundations and athlete development',
      'Movement and Kuzushi',
      'Throwing mechanics',
      'Ashi-waza',
      'Hip throws and turning throws',
      'Combinations and reactions',
      'Newaza and transitions',
      'Gripping and tactical development',
      'Randori and decision-making',
      'Mixed-level coaching',
      'Technical review and progression',
      'Assessment and coaching application',
    ],
    afterBullets: 'Each session is designed to be practical, structured and easy to follow.',
    result:
      'Instead of asking, “What am I going to teach tomorrow?” you open the system and start with a complete session.',
    popular: true,
  },
]
